import { FormInput } from "@/components/ui/FormField";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { X } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import type { InviteeFormSchemaType } from "../utils/inviteeValidators";
import { RELATIONSHIP_TYPE_LABELS } from "../types/invitee";
import { showInfo } from "@/lib/utils/notifications";
import styles from "../styles/inviteeAdd.module.css";
import {
  sanitizeInviteIncomeRawInput,
  formatInviteIncomeWhileTyping,
} from "../utils/inviteeFormatters";

interface CoApplicantFormProps {
  form: UseFormReturn<InviteeFormSchemaType>;
  index: number;
  onRemove?: () => void;
  onCheckDuplicate?: () => void;
}

export function CoApplicantForm({
  form,
  index,
  onRemove,
  onCheckDuplicate,
}: CoApplicantFormProps) {
  const {
    register,
    watch,
    setValue,
    trigger,
    clearErrors,
    formState: { errors },
  } = form;

  const coApplicantErrors = errors.co_applicant?.[index];
  const livingUnit = watch(`co_applicant.${index}.living_unit`);
  const isApprovedRequire = watch(`co_applicant.${index}.is_approved_require`);
  const incomeVerified = watch(`co_applicant.${index}.income_verified`);
  const income = watch(`co_applicant.${index}.income`);
  const relationshipType = watch(`co_applicant.${index}.relationship_type`);
  const relationshipOptions = Object.entries(RELATIONSHIP_TYPE_LABELS).map(
    ([value, label]) => ({ value, label }),
  );

  // Show info notification for co-signer
  const handleRelationshipChange = (value: string) => {
    setValue(`co_applicant.${index}.relationship_type`, value as any, {
      shouldValidate: true,
    });
    if (value === "CO_SIGNER") {
      showInfo(
        "Please note, selecting co-signer means that the applicant will not be moving into the selected unit.",
      );
    }
  };

  // Show alert for Liberty approval not needed
  const handleApprovalChange = (value: string) => {
    setValue(`co_applicant.${index}.is_approved_require`, value as "0" | "1", {
      shouldValidate: false,
    });
    // Reset dependent income fields silently; submit validation will surface required choices.
    setValue(`co_applicant.${index}.income_verified`, "", {
      shouldValidate: false,
    });
    setValue(`co_applicant.${index}.income`, "", {
      shouldValidate: false,
    });
    clearErrors([
      `co_applicant.${index}.income_verified`,
      `co_applicant.${index}.income`,
    ]);
  };

  // Handle income change - auto-select verified when typing (like Angular's incomeChange)
  const handleIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = sanitizeInviteIncomeRawInput(e.target.value);
    setValue(`co_applicant.${index}.income`, raw, { shouldValidate: true });
    setValue(`co_applicant.${index}.income_verified`, "1", {
      shouldValidate: true,
    });
  };

  // Handle income verified change - clear income if "Not Verified" (like Angular's incomeVerfiedChange)
  const handleIncomeVerifiedChange = (value: string) => {
    setValue(`co_applicant.${index}.income_verified`, value as "0" | "1", {
      shouldValidate: true,
    });
    if (value === "0") {
      setValue(`co_applicant.${index}.income`, "", { shouldValidate: true });
    }
  };

  return (
    <div className={styles.coApplicantForm}>
      {onRemove && (
        <button
          type="button"
          data-testid={`invitee-add-co-applicant-${index}-remove-button`}
          onClick={onRemove}
          className={styles.coApplicantRemoveButton}
          aria-label={`Remove co-applicant ${index + 1}`}
        >
          <X className="h-5 w-5" />
        </button>
      )}

      <h3
        data-testid={`invitee-add-co-applicant-${index}-title`}
        className={styles.coApplicantTitle}
      >
        Co-Applicant {index + 1}
      </h3>

      <div>
        {/* Name Fields */}
        <div className={styles.formRow}>
          <div className={`${styles.formCol} ${styles.formColOffset}`}>
            <div className={styles.formGroup}>
              <label
                className={styles.formLabel}
                htmlFor={`co_applicant_${index}_first_name`}
                data-testid={`invitee-add-co-applicant-${index}-first-name-label`}
              >
                First Name<span className={styles.requiredStar}>*</span>
              </label>
              {(() => {
                const { onBlur: registerOnBlur, ...rest } = register(`co_applicant.${index}.first_name`);
                return (
                  <FormInput
                    {...rest}
                    data-testid={`invitee-add-co-applicant-${index}-first-name-input`}
                    error={coApplicantErrors?.first_name?.message}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      registerOnBlur(e);
                      void trigger(`co_applicant.${index}.first_name`);
                    }}
                    className={styles.customFormControl}
                  />
                );
              })()}
            </div>
          </div>
          <div className={styles.formCol}>
            <div className={styles.formGroup}>
              <label
                className={styles.formLabel}
                htmlFor={`co_applicant_${index}_last_name`}
                data-testid={`invitee-add-co-applicant-${index}-last-name-label`}
              >
                Last Name<span className={styles.requiredStar}>*</span>
              </label>
              {(() => {
                const { onBlur: registerOnBlur, ...rest } = register(`co_applicant.${index}.last_name`);
                return (
                  <FormInput
                    {...rest}
                    data-testid={`invitee-add-co-applicant-${index}-last-name-input`}
                    error={coApplicantErrors?.last_name?.message}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      registerOnBlur(e);
                      void trigger(`co_applicant.${index}.last_name`);
                    }}
                    className={styles.customFormControl}
                  />
                );
              })()}
            </div>
          </div>
        </div>

        {/* Contact Fields */}
        <div className={styles.formRow}>
          <div className={`${styles.formCol} ${styles.formColOffset}`}>
            <div className={styles.formGroup}>
              <label
                className={styles.formLabel}
                htmlFor={`co_applicant_${index}_mobile_no`}
                data-testid={`invitee-add-co-applicant-${index}-mobile-label`}
              >
                Mobile Number (For Text Message)
                <span className={styles.requiredStar}>*</span>
              </label>
              <PhoneInput
                data-testid={`invitee-add-co-applicant-${index}-mobile-input`}
                value={watch(`co_applicant.${index}.mobile_no`) || ""}
                onChange={(value) =>
                  setValue(`co_applicant.${index}.mobile_no`, value, {
                    shouldValidate: true,
                  })
                }
                onBlur={() => {
                  trigger(`co_applicant.${index}.mobile_no`);
                  onCheckDuplicate?.();
                }}
                error={coApplicantErrors?.mobile_no?.message}
                className={styles.customFormControl}
              />
            </div>
          </div>
          <div className={styles.formCol}>
            <div className={styles.formGroup}>
              <label
                className={styles.formLabel}
                htmlFor={`co_applicant_${index}_email`}
                data-testid={`invitee-add-co-applicant-${index}-email-label`}
              >
                Email<span className={styles.requiredStar}>*</span> (MUST BE A
                VALID EMAIL ADDRESS)
              </label>
              {(() => {
                const {
                  onBlur: registerOnBlur,
                  onChange: registerOnChange,
                  ...rest
                } = register(`co_applicant.${index}.email`);
                return (
                  <FormInput
                    {...rest}
                    data-testid={`invitee-add-co-applicant-${index}-email-input`}
                    type="text"
                    autoComplete="email"
                    error={coApplicantErrors?.email?.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.toLowerCase();
                      registerOnChange(e);
                      void trigger(`co_applicant.${index}.email`);
                    }}
                    onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                      registerOnBlur(e);
                      const isValidEmail = await trigger(`co_applicant.${index}.email`);
                      if (isValidEmail) {
                        onCheckDuplicate?.();
                      }
                    }}
                    className={styles.customFormControl}
                  />
                );
              })()}
            </div>
          </div>
        </div>

        {/* Relationship */}
        <div className={styles.formRow}>
          <div className={`${styles.formCol} ${styles.formColOffset} ${styles.formColMatchFirstName}`}>
            <div className={styles.formGroup}>
              <label
                className={styles.formLabel}
                htmlFor={`co_applicant_${index}_relationship_type`}
                data-testid={`invitee-add-co-applicant-${index}-relationship-label`}
              >
                Relationship<span className={styles.requiredStar}>*</span>
              </label>
              <SearchableSelect
                triggerDataTestId={`invitee-add-co-applicant-${index}-relationship-select`}
                options={relationshipOptions}
                value={relationshipType || ""}
                onChange={(value) =>
                  handleRelationshipChange(Array.isArray(value) ? value[0] : value)
                }
                placeholder="Select Relationship"
                searchPlaceholder="Search Relationship"
                onBlur={() => trigger(`co_applicant.${index}.relationship_type`)}
                error={coApplicantErrors?.relationship_type?.message}
                className={styles.customFormControl}
              />
            </div>
          </div>
        </div>

        {/* Living in Unit */}
        <div className={styles.formRow}>
          <div className={`${styles.formCol} ${styles.formColOffset}`}>
            <div className={styles.formGroup}>
              <span
                className={styles.formLabel}
                data-testid={`invitee-add-co-applicant-${index}-living-unit-label`}
              >
                Will the applicant be living in the unit?
                <span className={styles.requiredStar}>*</span>
              </span>
              <div
                className={styles.radioButtonsContainer}
                style={{ marginTop: "8px" }}
              >
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name={`co_applicant.${index}.living_unit`}
                    value="1"
                    checked={livingUnit === "1"}
                    onChange={() =>
                      setValue(`co_applicant.${index}.living_unit`, "1", {
                        shouldValidate: true,
                      })
                    }
                    className={styles.radioInput}
                    data-testid={`invitee-add-co-applicant-${index}-living-unit-yes-radio`}
                  />
                  <span className={styles.radioLabel}>Yes</span>
                </label>
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name={`co_applicant.${index}.living_unit`}
                    value="0"
                    checked={livingUnit === "0"}
                    onChange={() =>
                      setValue(`co_applicant.${index}.living_unit`, "0", {
                        shouldValidate: true,
                      })
                    }
                    className={styles.radioInput}
                    data-testid={`invitee-add-co-applicant-${index}-living-unit-no-radio`}
                  />
                  <span className={styles.radioLabel}>No</span>
                </label>
              </div>
              {coApplicantErrors?.living_unit && (
                <p className="mt-1 text-sm text-red-600">
                  {coApplicantErrors.living_unit.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Liberty Approval Needed */}
        <div className={styles.formRow}>
          <div className={`${styles.formCol} ${styles.formColOffset}`}>
            <div className={styles.formGroup}>
              <span
                className={styles.formLabel}
                data-testid={`invitee-add-co-applicant-${index}-liberty-approval-label`}
              >
                Liberty Approval Needed?
                <span className={styles.requiredStar}>*</span>
              </span>
              <div
                className={styles.radioButtonsContainer}
                style={{ marginTop: "8px" }}
              >
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name={`co_applicant.${index}.is_approved_require`}
                    value="1"
                    checked={isApprovedRequire === "1"}
                    onChange={() => handleApprovalChange("1")}
                    className={styles.radioInput}
                    data-testid={`invitee-add-co-applicant-${index}-liberty-approval-yes-radio`}
                  />
                  <span className={styles.radioLabel}>Yes</span>
                </label>
                <label className={styles.radioInline}>
                  <input
                    type="radio"
                    name={`co_applicant.${index}.is_approved_require`}
                    value="0"
                    checked={isApprovedRequire === "0"}
                    onChange={() => handleApprovalChange("0")}
                    className={styles.radioInput}
                    data-testid={`invitee-add-co-applicant-${index}-liberty-approval-no-radio`}
                  />
                  <span className={styles.radioLabel}>No</span>
                </label>
              </div>
              {coApplicantErrors?.is_approved_require && (
                <p className="mt-1 text-sm text-red-600">
                  {coApplicantErrors.is_approved_require.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Alert message when Liberty Approval is not needed */}
        {isApprovedRequire === "0" && (
          <div
            className={styles.formRow}
            data-testid={`invitee-add-co-applicant-${index}-liberty-approval-note`}
          >
            <div
              className={styles.inlineInfoNote}
            >
              *Only applicants who apply with Liberty Rent and are approved will
              appear on the policy. If your property requires all residents to
              be listed on the policy please select 'Yes'.
            </div>
          </div>
        )}

        {/* Note about income when Liberty Approval is not needed and income is empty */}
        {isApprovedRequire === "0" && !income && (
          <div
            className={styles.formRow}
            data-testid={`invitee-add-co-applicant-${index}-income-verification-note`}
          >
            <div
              className={styles.inlineInfoNote}
            >
              Note: we need the income verified for co-applicant with "Liberty
              Approval Required" as "No".
            </div>
          </div>
        )}

        {/* Income Verification - Always shown, but "Not Verified" option only shows when Liberty Approval is needed */}
        <div
          data-testid={`invitee-add-co-applicant-${index}-income-verification-section`}
          className={styles.formRow}
          style={{ marginTop: "20px" }}
        >
          <div className={`${styles.formCol} ${styles.formColOffset}`}>
            <div className={styles.formGroup}>
              <span
                className={styles.formLabel}
                data-testid={`invitee-add-co-applicant-${index}-income-verification-label`}
              >
                Co-Applicant Income Verification
                <span className={styles.requiredStar}>*</span>
              </span>
              <div className={styles.incomeVerificationContainer}>
                <div className={styles.incomeRadioOption}>
                  <div className={styles.incomeRadioLabel}>
                    <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", margin: 0 }}>
                      <input
                        type="radio"
                        name={`co_applicant_${index}_income_verified`}
                        value="1"
                        checked={incomeVerified === "1"}
                        onChange={() => handleIncomeVerifiedChange("1")}
                        className={styles.radioInput}
                        data-testid={`invitee-add-co-applicant-${index}-income-verified-yes-radio`}
                      />
                      <span className={styles.incomeRadioText}>
                        Monthly Income verified as
                      </span>
                    </label>
                    <div
                      style={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "baseline",
                        marginLeft: "4px",
                        marginRight: "4px",
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          left: "4px",
                          fontSize: "13px",
                          color: "#253c5c",
                          pointerEvents: "none",
                          zIndex: 1,
                          top: "50%",
                          transform: "translateY(-50%)",
                          lineHeight: "1",
                        }}
                      >
                        $
                      </span>
                      <input
                        type="text"
                        value={formatInviteIncomeWhileTyping(
                          sanitizeInviteIncomeRawInput(income || ""),
                        )}
                        onChange={handleIncomeChange}
                        placeholder="Income Total"
                        disabled={false}
                        className={styles.incomeInput}
                        style={{ paddingLeft: "16px" }}
                        data-testid={`invitee-add-co-applicant-${index}-income-input`}
                      />
                    </div>
                    <span className={styles.incomeRadioText}>
                      (approximately)
                    </span>
                  </div>
                </div>
                {/* Not Verified option - only show when Liberty Approval is needed (matching Angular) */}
                {isApprovedRequire !== '0' && (
                  <div className={styles.incomeRadioOption}>
                    <div className={styles.incomeRadioLabel}>
                      <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", margin: 0 }}>
                        <input
                          type="radio"
                          name={`co_applicant_${index}_income_verified`}
                          value="0"
                          checked={incomeVerified === "0"}
                          onChange={() => handleIncomeVerifiedChange("0")}
                          className={styles.radioInput}
                          data-testid={`invitee-add-co-applicant-${index}-income-verified-no-radio`}
                        />
                        <span className={styles.incomeRadioText}>
                          Not Verified
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              {coApplicantErrors?.income_verified && (
                <div className={styles.errorMessage}>
                  <i className="fa fa-exclamation-circle fa-lg" />
                  {coApplicantErrors.income_verified.message}
                </div>
              )}
              {coApplicantErrors?.income && (
                <div className={styles.errorMessage}>
                  <i className="fa fa-exclamation-circle fa-lg" />
                  {coApplicantErrors.income.message}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
